import React from 'react';
import {Container, Header} from '@amzn/awsui-components-react';
import ReactEcharts from 'echarts-for-react';
import {SeriesOption} from 'echarts/types/dist/shared';
import {chartColorsLight} from '../../common/colors';
import type {ForecastBand} from '../model-explainer';

const COMPONENT_TEXTS = {
    chart_description: 'Each metric shows the contribution to the final forecast.',
} as const;

/**
 * The set of series varies with the data: bands and actuals appear only when
 * the window holds their columns. ECharts' default merge keeps any series the
 * previous option declared but the new one omits, so narrowing the window
 * from the forecast into the actuals period left the old bands (and their
 * dates on the axis) behind. Replacing the series list on every update, with
 * stable ids so unchanged series are merged rather than recreated, keeps the
 * chart equal to its props while preserving the user's legend selections.
 */
const SERIES_REPLACE_MERGE = ['series'];

/**
 * Prediction-interval bands are drawn as a pair of stacked line series: an
 * invisible one carrying the lower bound, and a second carrying the interval
 * height with an area fill. The lower-bound series exists only to lift the fill
 * off the axis, so it is kept out of the legend and out of the tooltip by name.
 */
const BAND_BASE_SERIES_PREFIX = '__band_base__';

const BAND_FILL_COLOR = chartColorsLight[0];
/** Widest band first, so narrower bands are drawn on top with a stronger fill. */
const BAND_OPACITIES = [0.13, 0.22];
const ACTUALS_COLOR = chartColorsLight[6];

function toNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A band is renderable only when both of its bounds are present in the data. */
function resolveAvailableBands(data: any[], forecastBands: ForecastBand[]): ForecastBand[] {
    return forecastBands.filter((band) =>
        data.some((row) => toNumber(row[band.lowerColumn]) !== null && toNumber(row[band.upperColumn]) !== null)
    );
}

function hasColumnData(data: any[], column?: string): boolean {
    return !!column && data.some((row) => toNumber(row[column]) !== null);
}

/**
 * First date carrying a forecast value. Used to mark where the actuals end and
 * the forecast begins.
 */
function resolveForecastStartDate(data: any[], forecastColumns: string[]): string | undefined {
    return data.find((row) => forecastColumns.some((column) => toNumber(row[column]) !== null))?.balanceDate;
}

export default function ModelExplainerLineChart({
    data,
    forecastColumns,
    contributionColumns,
    friendlyNameMapping,
    forecastBands = [],
    actualsColumn,
}: {
    data: any[];
    forecastColumns: string[];
    contributionColumns: string[];
    friendlyNameMapping: Record<string, string>;
    forecastBands?: ForecastBand[];
    actualsColumn?: string;
}) {
    // Both additions are driven by what the data actually contains rather than
    // by configuration alone, so a model run produced before these insight
    // types existed renders exactly as it did before.
    const availableBands = resolveAvailableBands(data, forecastBands);
    const showActuals = hasColumnData(data, actualsColumn);
    const forecastStartDate = showActuals ? resolveForecastStartDate(data, forecastColumns) : undefined;

    const series: SeriesOption[] = forecastColumns.map((col) => ({
        id: `forecast:${col}`,
        name: friendlyNameMapping[col],
        type: 'line',
        encode: {
            x: 'balanceDate',
            y: col,
        },
        symbol: 'none',
        lineStyle: {
            width: 2,
        },
        xAxisIndex: 0,
        yAxisIndex: 0,
        emphasis: {
            disabled: true,
        },
    }));

    const series2: SeriesOption[] = contributionColumns.map((col) => ({
        id: `contribution:${col}`,
        name: friendlyNameMapping[col],
        type: 'line',
        encode: {
            x: 'balanceDate',
            y: col,
        },
        symbol: 'none',
        lineStyle: {
            width: 2,
        },
        xAxisIndex: 1,
        yAxisIndex: 1,
        emphasis: {
            disabled: true,
        },
    }));

    // Appended after the existing series so palette assignment for those is
    // unchanged; `z` keeps the fills behind the forecast lines regardless.
    const bandSeries: SeriesOption[] = availableBands.flatMap((band, index) => {
        const bounds = data.map((row) => ({
            balanceDate: row.balanceDate,
            lower: toNumber(row[band.lowerColumn]),
            upper: toNumber(row[band.upperColumn]),
        }));
        const opacity = BAND_OPACITIES[Math.min(index, BAND_OPACITIES.length - 1)];
        const stack = `forecast-band-${index}`;

        return [
            {
                id: `band-base:${band.label}`,
                name: `${BAND_BASE_SERIES_PREFIX}${band.label}`,
                type: 'line',
                stack,
                // The explainer does not clip the posterior, so a low-volume
                // node can have a negative lower bound. ECharts' default
                // 'samesign' strategy would then put the base and the fill in
                // different stacks and anchor the band at zero instead of the
                // true bound.
                stackStrategy: 'all',
                data: bounds.map(({balanceDate, lower, upper}) => [balanceDate, lower !== null && upper !== null ? lower : null]),
                symbol: 'none',
                lineStyle: {opacity: 0},
                silent: true,
                legendHoverLink: false,
                z: 1,
                xAxisIndex: 0,
                yAxisIndex: 0,
                emphasis: {disabled: true},
            },
            {
                id: `band:${band.label}`,
                name: band.label,
                type: 'line',
                stack,
                stackStrategy: 'all',
                data: bounds.map(({balanceDate, lower, upper}) => [
                    balanceDate,
                    lower !== null && upper !== null ? upper - lower : null,
                ]),
                symbol: 'none',
                lineStyle: {opacity: 0},
                areaStyle: {color: BAND_FILL_COLOR, opacity},
                silent: true,
                legendHoverLink: false,
                z: 1,
                xAxisIndex: 0,
                yAxisIndex: 0,
                emphasis: {disabled: true},
            },
        ] as SeriesOption[];
    });

    const actualsSeries: SeriesOption[] =
        showActuals && actualsColumn
            ? [
                  {
                      id: `actuals:${actualsColumn}`,
                      name: friendlyNameMapping[actualsColumn] ?? actualsColumn,
                      type: 'scatter',
                      data: data
                          .map((row) => ({balanceDate: row.balanceDate, value: toNumber(row[actualsColumn])}))
                          .filter(({value}) => value !== null)
                          .map(({balanceDate, value}) => [balanceDate, value]),
                      symbolSize: 6,
                      itemStyle: {color: ACTUALS_COLOR},
                      z: 3,
                      xAxisIndex: 0,
                      yAxisIndex: 0,
                      emphasis: {disabled: true},
                      // Always declared, with no data when the window ends
                      // before the forecast starts: a merge into the same
                      // series keeps an omitted markLine, so leaving it out
                      // would carry the previous window's marker over.
                      markLine: {
                          silent: true,
                          symbol: 'none',
                          data: forecastStartDate ? [{xAxis: forecastStartDate}] : [],
                          lineStyle: {type: 'dashed', color: ACTUALS_COLOR},
                          label: {formatter: 'Forecast start', position: 'insideEndTop'},
                      },
                  },
              ]
            : [];

    const forecastLegendData = [
        ...forecastColumns.map((col) => friendlyNameMapping[col]),
        ...availableBands.map((band) => band.label),
        ...(showActuals && actualsColumn ? [friendlyNameMapping[actualsColumn] ?? actualsColumn] : []),
    ];

    const actualsSeriesName = actualsColumn ? friendlyNameMapping[actualsColumn] ?? actualsColumn : undefined;
    const seriesNameToColumn = new Map<string, string>([
        ...forecastColumns.map((col) => [friendlyNameMapping[col], col] as [string, string]),
        ...contributionColumns.map((col) => [friendlyNameMapping[col], col] as [string, string]),
        ...(showActuals && actualsColumn && actualsSeriesName ? [[actualsSeriesName, actualsColumn] as [string, string]] : []),
    ]);
    const forecastPanelSeriesNames = new Set<string>([
        ...forecastColumns.map((col) => friendlyNameMapping[col]),
        ...(showActuals && actualsSeriesName ? [actualsSeriesName] : []),
    ]);

    const option = {
        dataset: {
            dimensions: [
                'balanceDate',
                ...contributionColumns,
                ...forecastColumns,
                ...availableBands.flatMap((band) => [band.lowerColumn, band.upperColumn]),
                ...(showActuals && actualsColumn ? [actualsColumn] : []),
            ],
            source: data,
        },
        tooltip: buildTooltipOption(
            data,
            seriesNameToColumn,
            forecastPanelSeriesNames,
            availableBands,
            showActuals ? actualsColumn : undefined
        ),
        axisPointer: {
            type: 'cross',
            link: [
                {
                    xAxisIndex: 'all',
                },
            ],
        },
        color: chartColorsLight,
        legend: [
            {
                data: forecastLegendData,
                top: '0%',
                left: 'center',
                type: 'scroll',
            },
            {
                data: contributionColumns.map((col) => friendlyNameMapping[col]),
                top: '53%',
                left: 'center',
                type: 'scroll',
            },
        ],
        grid: [
            {
                top: '7%',
                height: '35%',
            },
            {
                top: '60%',
                height: '35%',
            },
        ],
        xAxis: [
            {
                gridIndex: 0,
                type: 'category',
                name: 'Date',
                nameLocation: 'middle',
                nameGap: 30,
                boundaryGap: false,
            },
            {
                gridIndex: 1,
                type: 'category',
                name: 'Date',
                nameLocation: 'middle',
                nameGap: 30,
                boundaryGap: false,
            },
        ],
        yAxis: [
            {
                gridIndex: 0,
                type: 'value',
                name: 'Value',
                nameLocation: 'middle',
                nameGap: 50,
            },
            {
                gridIndex: 1,
                type: 'value',
                name: 'Value',
                nameLocation: 'middle',
                nameGap: 50,
            },
        ],
        series: [...series, ...series2, ...bandSeries, ...actualsSeries] as SeriesOption[],
    };

    return (
        <Container
            header={
                <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                    Forward Looking Explainability
                </Header>
            }
        >
            <ReactEcharts
                option={option}
                replaceMerge={SERIES_REPLACE_MERGE}
                style={{height: '50vh'}}
                opts={{renderer: 'svg'}}
                data-testid="page-line-chart"
            />
        </Container>
    );
}

/**
 * Without bands or actuals the tooltip is left exactly as it was. With them, a
 * formatter is needed: the band series carry stacked offsets rather than the
 * quantile values themselves, so their bounds are read back from the row and
 * rendered as a range, and the invisible lower-bound helper series is dropped.
 */
function buildTooltipOption(
    data: any[],
    seriesNameToColumn: Map<string, string>,
    forecastPanelSeriesNames: Set<string>,
    availableBands: ForecastBand[],
    actualsColumn?: string
) {
    if (availableBands.length === 0 && !actualsColumn) {
        return {
            trigger: 'axis',
            valueFormatter: (value: number) => value?.toFixed(0),
        };
    }

    const bandLabels = new Set(availableBands.map((band) => band.label));
    const rowsByDate = new Map<string, any>(data.map((row) => [row.balanceDate, row]));
    const format = (value: number | null) => (value === null ? '-' : value.toFixed(0));

    return {
        trigger: 'axis',
        formatter: (params: any) => {
            const entries = Array.isArray(params) ? params : [params];
            if (entries.length === 0) {
                return '';
            }

            const axisValue = entries[0].axisValue;
            const row = rowsByDate.get(axisValue);
            const lines = [`<b>${axisValue}</b>`];

            // Values are read from the row by column rather than from the
            // ECharts payload, which differs in shape between dataset-encoded
            // series and the band/actuals series that carry their own data.
            entries
                .filter((entry: any) => !entry.seriesName?.startsWith(BAND_BASE_SERIES_PREFIX) && !bandLabels.has(entry.seriesName))
                .forEach((entry: any) => {
                    const column = seriesNameToColumn.get(entry.seriesName);
                    const value = column && row ? toNumber(row[column]) : null;
                    lines.push(`${entry.marker} ${entry.seriesName}: ${format(value)}`);
                });

            // Bands belong to the forecast panel only; suppress them when the
            // pointer is over the contributions panel.
            const hoveringForecastPanel = entries.some((entry: any) => forecastPanelSeriesNames.has(entry.seriesName));
            if (row && hoveringForecastPanel) {
                availableBands.forEach((band) => {
                    const lower = toNumber(row[band.lowerColumn]);
                    const upper = toNumber(row[band.upperColumn]);
                    if (lower !== null && upper !== null) {
                        lines.push(`${band.label}: ${format(lower)} – ${format(upper)}`);
                    }
                });
            }

            return lines.join('<br/>');
        },
    };
}
